import { createAsyncThunk } from '@reduxjs/toolkit';

import { formatArrayToFilterOptions } from '../../helpers/common';

import { GROUP_TYPE } from '../../constants/group';

import { LabelService } from '../../services';

export const createLabel = createAsyncThunk('label/createLabel', async (data, { rejectWithValue }) => {
  if (data.main_type !== 'collection') {
    delete data.filters;
  }

  try {
    const response = await LabelService.createLabel(data);

    return response?.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Failed to create label');
  }
});

export const addAnimalsToGroup = createAsyncThunk(
  'label/addAnimalsToGroup',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await LabelService.addAnimalsToGroup({ id, data });

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to add animals to the group');
    }
  }
);

export const fetchLabels = createAsyncThunk('label/fetchLabels', async (params, { rejectWithValue }) => {
  try {
    const { data } = await LabelService.fetchLabels(params);

    return {
      data,
      options: data?.map(x => ({ value: x.id, label: x.name, color: x.colour })),
      filterOptions: formatArrayToFilterOptions(data, 'name', 'id'),
      customGroupOptions: data
        .filter(label => label.type === GROUP_TYPE.CUSTOM)
        .map(x => ({
          farm_id: x.farm_id,
          value: x.id,
          label: x.name,
          color: x.colour,
          main_type: x.main_type,
          type: GROUP_TYPE.CUSTOM
        }))
    };
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Failed to fetch labels');
  }
});

export const fetchLabel = createAsyncThunk('label/fetchLabel', async ({ id, params }, { rejectWithValue }) => {
  try {
    const { data } = await LabelService.fetchLabel({ id, params });

    return data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Failed to fetch label');
  }
});

export const updateGroup = createAsyncThunk('label/updateGroup', async ({ id, data }, { rejectWithValue }) => {
  if (data.main_type !== 'collection') {
    delete data.filters;
  }

  try {
    const response = await LabelService.updateLabel({ id, data });

    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Failed to update the group');
  }
});

export const deleteLabels = createAsyncThunk('label/deleteLabels', async (ids, { rejectWithValue }) => {
  try {
    await LabelService.deleteLabels(ids);

    return ids;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Failed to delete labels');
  }
});

export const removeAnimalsFromGroup = createAsyncThunk(
  'label/removeAnimalsFromGroup',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await LabelService.removeAnimalsFromGroup({ id, data });

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to remove animals from the group');
    }
  }
);

/**
 * NOT USED, Kept when/if we decide to rework the logic of saving filters if we do not rework the query builder
 * Updates filters to have id instead of name
 * @param {*} data Group object 
 * @returns Updated group object
 */
const updateFiltersInGroup = (data) => {
  if (!data || !data.filters) {
    return data
  }

  // The whole logic in app is built to pass the group id as value in filters, eventhough we use "name" as property
  // In order to keep existing logic and create the correct query need to replace "name" with property field ("id")
  const updatedGroups = data.filters.groups.map(filterGroup => {
    const updatedConditions = filterGroup.conditions.map(condition => {
      return {
         ...condition,
        field: condition.field === 'name' ? 'id' : condition.field
      };
    });

    return {
      ...filterGroup,
      conditions: updatedConditions
    };
  });

  return {
    ...data,
    filters: {
      ...data.filters,
      groups: updatedGroups
    }
  };
}
